import screenfull from 'screenfull';
import Application from './Application';
import common from './common';
import PreloaderScene from './scenes/PreloaderScene';
import Assets from './assets';
import StatHUD from './StatHUD';
import Storage from './storage';
import Brim from './brim';
import {translate} from './utils';
import Tracker from './Tracker';

export default class Main {
	/**
     * @param {number} [width=1024]
     * @param {number} [height=768]
	 */
	constructor() {
		this._app = null;
		this._preloader = null;

		this._statHUD = null;
		common.assets = null;

		this.setupStorage();

		P3.audio.mute(common.storage.data.muted);
	}

	setupStorage() {
		const version = '0.5.1';

		common.storage = new Storage('com.p3.ben10brawler');

		common.storage.init();
		common.storage.load();

		if (Object.keys(common.storage.data).length === 0 || common.storage.data.version !== version) {
			common.storage.removeAllById();

			const defaultData = {
				version  : version,
				muted    : false,
				intro    : true,
				language : 'en'
			};

			common.storage.setData(defaultData);
			common.storage.save();
		}
	}

	init() {
		P3.settings.WARN_TEXTURE_DIVISIBLE_BY_2 = false;
		P3.assets.audioFileTypes = ['ogg', 'mp3'];

		PIXI.settings.RETINA_PREFIX = /(?=[^_]*$)(.+)x/;
		PIXI.settings.MIPMAP_TEXTURES = false;

		THREE.Cache.enabled = false;

		if (PIXI.utils.isMobile.android.device && screenfull.enabled) {
			screenfull.on('change', () => {
				setTimeout(() => {
					common.view.resize();
				}, 1000);
			});
		}

		if (typeof TweenMax !== 'undefined') {
			(TweenMax.defaultOverwrite = 'none');
		}

		const options =
        {
        	width                 : common.STAGE_WIDTH,
        	height                : common.STAGE_HEIGHT,
        	holderId              : 'game',
        	portrait              : false,
        	rotateImageUrl        : './assets/images/rotate_1x.jpg',
        	rotateImageColor      : '#000000',
        	aspectRatioFillHeight : true
        };

		common.view = new P3.View(options);

		common.t = new Tracker();

		common.view.signals.ready.once((canvas) => {
			let ua = navigator.userAgent;
			let badShaders = false;

			for (const id of common.config.blacklisted_UAs) {		// If our UA matches one of the blacklisted (bad performance) devices, enable mediump shaders
				if (ua.includes(id)) {
					badShaders = true;
					break;
				}
			}

			// three
			const shaderPrecision = badShaders ? 'mediump' : 'highp';
			console.log(`Shader precision: ${shaderPrecision}`);
			common.THREErenderer = new THREE.WebGLRenderer({precision: shaderPrecision});
			common.THREErenderer.setSize(options.width, options.height);
			common.THREErenderer.autoClear = true;

			common.THREEstage = new THREE.Scene();

			common.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.05, 100);

			common.assets = new Assets();
			common.assets.signals.complete.once(this.load3AssetsComplete, this);

			// pixi
			const pixiOptions =
            {
            	view                  : canvas,
            	transparent           : true,
            	preserveDrawingBuffer : false,
            	resolution            : 1,
            	powerPreference       : 'high-performance'
            };

			common.renderer = new PIXI.WebGLRenderer(window.innerWidth, window.innerHeight, pixiOptions);

			$('#game').prepend(common.THREErenderer.domElement);
			$('#game > canvas:nth-child(1)').css({position: 'absolute', left: '0px', right: '0px', top: '0px', bottom: '0px', width: '100%', height: '100%', overflow: 'visible', display: 'block', touchAction: 'none', cursor: 'inherit'});
			$('#game > canvas:nth-child(1)').attr('id', 'three-canvas');

			common.formats = {
				astc  : common.THREErenderer.extensions.get('WEBGL_compressed_texture_astc'),
				etc1  : common.THREErenderer.extensions.get('WEBGL_compressed_texture_etc1'),
				s3tc  : common.THREErenderer.extensions.get('WEBGL_compressed_texture_s3tc'),
				pvrtc : common.THREErenderer.extensions.get('WEBGL_compressed_texture_pvrtc')
			};

			common.stage = new PIXI.Container();
			common.stage.addChild(P3.scene.view);

			P3.input.init(common.stage, canvas);

			const inAppBrowser = navigator.userAgent.includes('Mobile/') && !navigator.userAgent.includes('Safari/');
			const isSafari = navigator.vendor && navigator.vendor.indexOf('Apple') > -1 &&
				navigator.userAgent &&
            	navigator.userAgent.indexOf('CriOS') === -1 &&
            	navigator.userAgent.indexOf('FxiOS') === -1;

			if (!inAppBrowser && isSafari) {
				window.brimElement = new Brim('game', '');
			}

			common.transitions = new PIXI.Container();
			common.stage.addChild(common.transitions);

			common.view.toggleRotate = (value) => {
				common.view._rotateImage.style.display = value ? 'block' : 'none';
				common.THREErenderer.domElement.style.display = value ? 'none' : 'block';
				common.view._canvas.style.display = value ? 'none' : 'block';
			};

			common.ticker = new P3.Ticker();
			common.ticker.init(() => {
				common.assets.update();

				P3.scene.update();
				P3.input.update();
				P3.utils.TWEEN.update(); // comment this line out if you are using TweenMax
			}, () => {
				common.THREErenderer.render(common.THREEstage, common.camera);
				common.renderer.render(common.stage);

				if (this._statHUD) {
					this._statHUD.update(common.THREErenderer.info);
				}
			}, this);

			this.loadPreAssets();
		}, this);

		common.view.signals.resize.add(this.onCanvasResize, this);

		// we load the config before we do anything
		this.loadConfig(() => {
			common.view.init();
		}, this);
	}

	loadConfig(callback, scope) {
		scope = scope || window;

		P3.assets.addFiles([{name: 'config', url: 'config.json'}], './');
		P3.assets.addFiles([{name: 'strings', url: 'strings.json'}], './assets/data/');

		P3.assets.signals.complete.once(() => {
			common.config = P3.assets.json('config');
			common.config.strings = P3.assets.json('strings');
			common.config.language = localStorage.getItem('language');
			common.config.competition_enabled = common.config.competition_enabled && !common.config.competition_blacklist.includes(common.config.language);

			console.log(`Current language: ${common.config.language}, Competition Enabled? ${common.config.competition_enabled}`);

			common.t.gameTitle = `ben 10 ${translate('GAME.TITLE')}`;
			common.t.track('game launch event', {gamelaunch: 1});
			common.t.loadStart = Date.now();

			if (['bg', 'ru'].includes(common.config.language)) {
				common.commandFontSize = ~~(14 * common.commandScale);

				// Client complained that Bulgarian/Russian words are too close together so we override the setter and add some more spaces.
				let proto = PIXI.extras.BitmapText.prototype;
				Object.defineProperty(proto, 'text', {
					get: function() {
						return this._text;
					},
					set: function(text) {
						const words = text.split(' ');		// split the wanted text into array by spaces
						text = words.length >= 1 ? words.join('   ') : text;	// then join it again with more spaces

						text = String(text === null || text === undefined ? '' : text);	// Pixi's default setter stuff
						if (this._text === text) {
							return;
						}

						this._text = text;
						this.dirty = true;
					}
				});
			}

			if (callback) {
				callback.call(scope);
			}
		}, this);

		P3.assets.load();
	}

	loadPreAssets() {
		const files =
        [
        	{name: 'ui_preloader_1x', url: 'images/texture_sheets/ui_preloader_1x.json'},
        	{name: 'ui_bg_1x', url: 'images/texture_sheets/ui_bg_1x.json'},
        	{name: 'Ahkio_regular_42pt', url: 'fonts/Ahkio_regular_42pt.fnt'},
        	{name: 'Ahkio_thin_48pt', url: 'fonts/Ahkio_thin_48pt.fnt'},
        	{name: 'ahkio_180_white_numbers', url: 'fonts/ahkio_180_white_numbers.fnt'},
        	{name: 'PTSans_36pt', url: 'fonts/PTSans_36pt.fnt'},
        	{name: 'title_90', url: 'fonts/title_90.fnt'},
        	{name: 'Zcool_64pt', url: 'fonts/Zcool_64pt.fnt'}
        ];

		if (files.length) {
			P3.assets.addFiles(files, './assets/');
			P3.assets.signals.complete.once(this.loadAssets, this);
			P3.assets.load();
		} else {
			this.loadAssets();
		}
	}

	loadAssets() {
		const files = [
        	// sheets
        	{name: 'ui_competition_1x', url: 'images/texture_sheets/ui_competition_1x.json'},
        	{name: 'ui_functions_1x', url: 'images/texture_sheets/ui_functions_1x.json'},
        	{name: 'ui_help_1x', url: 'images/texture_sheets/ui_help_1x.json'},
        	{name: 'ui_menu_1x', url: 'images/texture_sheets/ui_menu_1x.json'},
        	{name: 'ui_buttons_1x', url: 'images/texture_sheets/ui_buttons_1x.json'},
        	{name: 'ui_intro_1x', url: 'images/texture_sheets/ui_intro_1x.json'},
        	{name: 'ui_transform_1x', url: 'images/texture_sheets/ui_transform_1x.json'},
        	{name: 'particles_1x', url: 'images/texture_sheets/particles_1x.json'},

        	{name: 'test_room_corridor', url: 'data/levels/test_room_corridor.json'},
        	{name: 'level_data', url: 'data/levels.json'},
        	{name: 'animations', url: 'data/animations.json'},
        	{name: 'tutorial', url: 'spine/tutorial.json'},
        	{name: 'char_ben', url: 'spine/char_ben-37.json'},

        	// PIXI Particles
        	{name: 'particle_endlvl_green', url: 'particles/particle_endlvl_green.json'},
        	{name: 'particle_endlvl_blue', url: 'particles/particle_endlvl_blue.json'},
        	{name: 'particle_2d_results_star_appear_00', url: 'particles/particle_2d_results_star_appear_00.json'},
        	{name: 'particle_2d_competiton_button_attract_00', url: 'particles/particle_2d_competiton_button_attract_00.json'},

        	// THREE Particles
        	{name: 'particle_3d_coin_collect_burst', url: 'particles/particle_3d_coin_collect_burst.json'},
        	{name: 'particle_3d_data_emitter_00', url: 'particles/particle_3d_data_emitter_00.json'},
        	{name: 'particle_3d_data_emitter_01', url: 'particles/particle_3d_data_emitter_01.json'},
        	{name: 'particle_acid_patch_bubbles', url: 'particles/particle_acid_patch_bubbles.json'},
        	{name: 'particle_barrel_on_fire_1', url: 'particles/particle_barrel_on_fire_1.json'},
        	{name: 'particle_fire_horizontal', url: 'particles/particle_fire_horizontal.json'},
        	{name: 'particle_fire_pit_vertical', url: 'particles/particle_fire_pit_vertical.json'},
        	{name: 'particle_fire_vertical', url: 'particles/particle_fire_vertical.json'},
        	{name: 'particle_fire_wall_torch', url: 'particles/particle_fire_wall_torch.json'},
        	{name: 'particle_ground_explosion_1', url: 'particles/particle_ground_explosion_1.json'},
        	{name: 'particle_ice_patch_frost', url: 'particles/particle_ice_patch_frost.json'},
        	{name: 'particle_pickup_burst_green', url: 'particles/particle_pickup_burst_green.json'},
        	{name: 'particle_pickup_burst_green2', url: 'particles/particle_pickup_burst_green2.json'},
        	{name: 'particle_sparkle_attract', url: 'particles/particle_sparkle_attract.json'},
        	{name: 'particle_steam_horizontal', url: 'particles/particle_steam_horizontal.json'},
        	{name: 'particle_steam_jet_horizontal', url: 'particles/particle_steam_jet_horizontal.json'},
        	{name: 'particle_steam_vertical', url: 'particles/particle_steam_vertical.json'},
        	{name: 'particle_water_splash', url: 'particles/particle_water_splash.json'},

        	// LEVELS
        	{name: 'stage_1', url: 'data/levels/stage_1.json'},
        	{name: 'stage_2', url: 'data/levels/stage_2.json'},
        	{name: 'stage_3', url: 'data/levels/stage_3.json'},
        	{name: 'stage_4', url: 'data/levels/stage_4.json'},
        	{name: 'stage_5', url: 'data/levels/stage_5.json'},
        	{name: 'stage_6', url: 'data/levels/stage_6.json'},
        	{name: 'stage_7', url: 'data/levels/stage_7.json'},
        	{name: 'stage_8', url: 'data/levels/stage_8.json'},
        	{name: 'stage_9', url: 'data/levels/stage_9.json'},
        	{name: 'stage_10', url: 'data/levels/stage_10.json'},
        	{name: 'stage_11', url: 'data/levels/stage_11.json'},
        	{name: 'stage_12', url: 'data/levels/stage_12.json'},
        	{name: 'stage_13', url: 'data/levels/stage_13.json'},
        	{name: 'stage_14', url: 'data/levels/stage_14.json'},
        	{name: 'stage_15', url: 'data/levels/stage_15.json'},
        	{name: 'stage_16', url: 'data/levels/stage_16.json'},
        	{name: 'stage_17', url: 'data/levels/stage_17.json'},
        	{name: 'stage_18', url: 'data/levels/stage_18.json'},
        	{name: 'stage_19', url: 'data/levels/stage_19.json'},
        	{name: 'stage_20', url: 'data/levels/stage_20.json'},
        	{name: 'stage_21', url: 'data/levels/stage_21.json'},
        	{name: 'stage_22', url: 'data/levels/stage_22.json'},
        	{name: 'stage_23', url: 'data/levels/stage_23.json'},
        	{name: 'stage_24', url: 'data/levels/stage_24.json'},
        	{name: 'stage_25', url: 'data/levels/stage_25.json'},
        	{name: 'stage_26', url: 'data/levels/stage_26.json'},
        	{name: 'stage_27', url: 'data/levels/stage_27.json'},
        	{name: 'stage_28', url: 'data/levels/stage_28.json'},

        	{name: 'level_select_bg_zone1_1x', url: 'images/level_select_bg_zone1_1x.jpg'},
        	{name: 'level_select_bg_zone2_1x', url: 'images/level_select_bg_zone2_1x.jpg'},
        	{name: 'level_select_bg_zone3_1x', url: 'images/level_select_bg_zone3_1x.jpg'},
        	{name: 'compImage', url: `${'images/competition/compImage_'}${common.config.language}.jpg`},
        	{name: 'endgame', url: 'images/endgame.jpg'},

        	// AUDIOs
        	{name: 'music_ben10_loop', url: `${'audio/music_ben10_loop'}${P3.assets.audioFileExt}`},
        	{name: 'music_gameplay_loop_01', url: `${'audio/music_gameplay_loop_01'}${P3.assets.audioFileExt}`},
        	{name: 'music_gameplay_loop_02', url: `${'audio/music_gameplay_loop_02'}${P3.assets.audioFileExt}`},
        	{name: 'music_level_complete_sting_00', url: `${'audio/music_level_complete_sting_00'}${P3.assets.audioFileExt}`},
        	{name: 'music_levels_loop_00', url: `${'audio/music_levels_loop_00'}${P3.assets.audioFileExt}`},
        	{name: 'music_splash_playsting_00', url: `${'audio/music_splash_playsting_00'}${P3.assets.audioFileExt}`},
        	{name: 'music_splash_screen_00', url: `${'audio/music_splash_screen_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_achievement_unlocked_00', url: `${'audio/sfx_achievement_unlocked_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_ambience_mine_00', url: `${'audio/sfx_ambience_mine_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_ambience_pads_00', url: `${'audio/sfx_ambience_pads_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_ambience_space_sewer_00', url: `${'audio/sfx_ambience_space_sewer_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_ambience_temple_00', url: `${'audio/sfx_ambience_temple_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_block_push_00', url: `${'audio/sfx_block_push_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_btn_play_00', url: `${'audio/sfx_btn_play_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_btn_press_00', url: `${'audio/sfx_btn_press_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_btn_rollover_00', url: `${'audio/sfx_btn_rollover_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_bump_into_block', url: `${'audio/sfx_bump_into_block'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_crate_explode_00', url: `${'audio/sfx_crate_explode_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_cannonbolt_jump_00', url: `${'audio/sfx_cannonbolt_jump_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_cannonbolt_land_00', url: `${'audio/sfx_cannonbolt_land_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_cannonbolt_roll_00', url: `${'audio/sfx_cannonbolt_roll_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_block_added', url: `${'audio/sfx_code_block_added'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_block_move_position', url: `${'audio/sfx_code_block_move_position'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_block_processed_success', url: `${'audio/sfx_code_block_processed_success'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_block_trashed', url: `${'audio/sfx_code_block_trashed'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_fail', url: `${'audio/sfx_code_fail'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_library_close', url: `${'audio/sfx_code_library_close'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_library_open', url: `${'audio/sfx_code_library_open'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_code_token_pickup_00', url: `${'audio/sfx_code_token_pickup_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_die_lava_00', url: `${'audio/sfx_die_lava_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_die_spike_00', url: `${'audio/sfx_die_spike_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_die_water_00', url: `${'audio/sfx_die_water_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_metal_00', url: `${'audio/sfx_footstep_heavy_metal_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_metal_01', url: `${'audio/sfx_footstep_heavy_metal_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_stone_00', url: `${'audio/sfx_footstep_heavy_stone_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_stone_01', url: `${'audio/sfx_footstep_heavy_stone_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_temple_00', url: `${'audio/sfx_footstep_heavy_temple_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_temple_01', url: `${'audio/sfx_footstep_heavy_temple_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_wood_00', url: `${'audio/sfx_footstep_heavy_wood_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_heavy_wood_01', url: `${'audio/sfx_footstep_heavy_wood_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_firetrap_loop_00', url: `${'audio/sfx_firetrap_loop_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_firetrap_loop_01', url: `${'audio/sfx_firetrap_loop_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_metal_00', url: `${'audio/sfx_footstep_metal_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_metal_01', url: `${'audio/sfx_footstep_metal_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_stone_00', url: `${'audio/sfx_footstep_stone_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_stone_01', url: `${'audio/sfx_footstep_stone_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_temple_00', url: `${'audio/sfx_footstep_temple_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_temple_01', url: `${'audio/sfx_footstep_temple_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_wood_00', url: `${'audio/sfx_footstep_wood_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_footstep_wood_01', url: `${'audio/sfx_footstep_wood_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_intro_panel_whoosh_00', url: `${'audio/sfx_intro_panel_whoosh_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_lavaflow_00', url: `${'audio/sfx_lavaflow_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_omnitrix_open_00', url: `${'audio/sfx_omnitrix_open_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_omnitrix_transform_00', url: `${'audio/sfx_omnitrix_transform_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_omnitrix_transform_back_01', url: `${'audio/sfx_omnitrix_transform_back_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_overflow_enterwater_00', url: `${'audio/sfx_overflow_enterwater_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_overflow_jump_00', url: `${'audio/sfx_overflow_jump_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_overflow_landsoft_00', url: `${'audio/sfx_overflow_landsoft_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_overflow_splash_00', url: `${'audio/sfx_overflow_splash_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_score_counter_00', url: `${'audio/sfx_score_counter_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_score_extramove_00', url: `${'audio/sfx_score_extramove_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_score_goodmove_00', url: `${'audio/sfx_score_goodmove_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_spike_down_00', url: `${'audio/sfx_spike_down_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_spike_up_00', url: `${'audio/sfx_spike_up_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_star_impact_00', url: `${'audio/sfx_star_impact_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_star_impact_01', url: `${'audio/sfx_star_impact_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_star_impact_02', url: `${'audio/sfx_star_impact_02'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_steamtrap_loop_01', url: `${'audio/sfx_steamtrap_loop_01'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_stinkfly_flap_00', url: `${'audio/sfx_stinkfly_flap_00'}${P3.assets.audioFileExt}`},
        	{name: 'sfx_terminal_typing_00', url: `${'audio/sfx_terminal_typing_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_hehehe_00', url: `${'audio/vo_ben_hehehe_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_hurt_00', url: `${'audio/vo_ben_hurt_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_jump_00', url: `${'audio/vo_ben_jump_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_land_heavy_00', url: `${'audio/vo_ben_land_heavy_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_lift_00', url: `${'audio/vo_ben_lift_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_oohoof_00', url: `${'audio/vo_ben_oohoof_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_release_00', url: `${'audio/vo_ben_release_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_surprise_00', url: `${'audio/vo_ben_surprise_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_whoa_00', url: `${'audio/vo_ben_whoa_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_win_haaa_00', url: `${'audio/vo_ben_win_haaa_00'}${P3.assets.audioFileExt}`},
        	{name: 'vo_ben_woohoo_00', url: `${'audio/vo_ben_woohoo_00'}${P3.assets.audioFileExt}`}
		];

		let folder = '';
		if (common.formats.pvrtc) {
			folder = 'pvrtc1_2';
		} else if (common.formats.etc1) {
			folder = 'etc1';
		} else if (common.formats.s3tc) {
			folder = 'dxt1';
		}

		const threefiles = [
			/**
			 * MODELS
			 */
			{name: 'wall_001', path: './assets/3d/wall.gltf', type: '3d'},
			{name: 'tile_001', path: './assets/3d/tile_001.gltf', type: '3d'},
			{name: 'lab_terminal', path: './assets/3d/lab_terminal.gltf', type: '3d'},
			{name: 'lab_crate_single', path: './assets/3d/lab_crate_single.gltf', type: '3d'},
			{name: 'lab_spikesanim', path: './assets/3d/lab_spikesanim.gltf', type: '3danim'},
			{name: 'mine_crate_single', path: './assets/3d/mine_crate_single.gltf', type: '3d'},
			{name: 'splinters', path: './assets/3d/splinters.gltf', type: '3d'},
			{name: 'omnitrix', path: './assets/3d/omnitrix.gltf', type: '3danim'},

			/**
			 * TEXTURES
			 */
			{name: 'Water_1_M_Normal', path: './assets/images/Water_1_M_Normal.jpg', type: 'texture'},
			{name: 'Water_2_M_Normal', path: './assets/images/Water_2_M_Normal.jpg', type: 'texture'},
			{name: 'fire_01', path: './assets/images/fire_01.png', type: 'texture'},
			{name: 'fire_02', path: './assets/images/fire_02.png', type: 'texture'},
			{name: 'default-particle', path: './assets/images/default-particle.png', type: 'texture'},
			{name: 'particle3', path: './assets/images/particle3.png', type: 'texture'},
			{name: 'smoke_01', path: './assets/images/smoke_01.png', type: 'texture'},
			{name: 'star_09', path: './assets/images/star_09.png', type: 'texture'},
			{name: 'flame_03', path: './assets/images/flame_03.png', type: 'texture'},
			{name: 'circle_01', path: './assets/images/circle_01.png', type: 'texture'},
			{name: 'cloud', path: './assets/images/cloud.png', type: 'texture'},
			{name: 'acid_puddle', path: './assets/images/acid_puddle.png', type: 'texture'},
			{name: 'lava_puddle', path: './assets/images/lava_puddle.png', type: 'texture'},
			{name: 'particle_explode_water_01', path: './assets/images/particle_explode_water_01.png', type: 'texture'},
			{name: 'particle_dust_128', path: './assets/images/particle_dust_128.png', type: 'texture'},
			{name: 'particle_coin_collect_00', path: './assets/images/particle_coin_collect_00.png', type: 'texture'},
			{name: 'particle_data_0', path: './assets/images/particle_data_0.png', type: 'texture'},
			{name: 'particle_data_1', path: './assets/images/particle_data_1.png', type: 'texture'},
			{name: 'tile_ice', path: './assets/3d/tile_ice.jpg', type: 'texture'},

			/**
			 * COMPRESSED TEXTURES
			 */
			{name: 'omnitrix_floor', path: `./assets/3d/compressed-textures/${folder}/omnitrix_floor.ktx`, type: 'ctexture'},
			{name: 'omnitrix_main', path: `./assets/3d/compressed-textures/${folder}/omnitrix.ktx`, type: 'ctexture'},
			{name: 'omnitrix_competition', path: `./assets/3d/compressed-textures/${folder}/omnitrix_competition.ktx`, type: 'ctexture'},
			{name: 'omnitrix_ben', path: `./assets/3d/compressed-textures/${folder}/omnitrix_ben.ktx`, type: 'ctexture'},
			{name: 'omnitrix_cannonbolt', path: `./assets/3d/compressed-textures/${folder}/omnitrix_cannonbolt.ktx`, type: 'ctexture'},
			{name: 'omnitrix_fourarms', path: `./assets/3d/compressed-textures/${folder}/omnitrix_fourarms.ktx`, type: 'ctexture'},
			{name: 'omnitrix_heatblast', path: `./assets/3d/compressed-textures/${folder}/omnitrix_heatblast.ktx`, type: 'ctexture'},
			{name: 'omnitrix_jump', path: `./assets/3d/compressed-textures/${folder}/omnitrix_jump.ktx`, type: 'ctexture'},
			{name: 'omnitrix_overflow', path: `./assets/3d/compressed-textures/${folder}/omnitrix_overflow.ktx`, type: 'ctexture'},
			{name: 'omnitrix_repeat', path: `./assets/3d/compressed-textures/${folder}/omnitrix_repeat.ktx`, type: 'ctexture'},
			{name: 'omnitrix_stinkfly', path: `./assets/3d/compressed-textures/${folder}/omnitrix_stinkfly.ktx`, type: 'ctexture'},
			{name: 'omnitrix_turn', path: `./assets/3d/compressed-textures/${folder}/omnitrix_turn.ktx`, type: 'ctexture'},
			{name: 'omnitrix_upgrade', path: `./assets/3d/compressed-textures/${folder}/omnitrix_upgrade.ktx`, type: 'ctexture'},
			{name: 'omnitrix_variable', path: `./assets/3d/compressed-textures/${folder}/omnitrix_variable.ktx`, type: 'ctexture'},
			{name: 'omnitrix_xlr8', path: `./assets/3d/compressed-textures/${folder}/omnitrix_xlr8.ktx`, type: 'ctexture'},
			{name: 'mine_crate', path: `./assets/3d/compressed-textures/${folder}/mine_crate.ktx`, type: 'ctexture'},
			{name: 'lab_crate', path: `./assets/3d/compressed-textures/${folder}/lab_crate.ktx`, type: 'ctexture'},
			{name: 'mine_conveyor', path: `./assets/3d/compressed-textures/${folder}/mine_conveyor.ktx`, type: 'ctexture'},
			{name: 'mine_spikes', path: `./assets/3d/compressed-textures/${folder}/mine_spikes.ktx`, type: 'ctexture'},
			{name: 'lab_conveyor', path: `./assets/3d/compressed-textures/${folder}/lab_conveyor.ktx`, type: 'ctexture'},
			{name: 'lab_floor_1', path: `./assets/3d/compressed-textures/${folder}/lab_floor_1.ktx`, type: 'ctexture'},
			{name: 'lab_floor_2', path: `./assets/3d/compressed-textures/${folder}/lab_floor_2.ktx`, type: 'ctexture'},
			{name: 'lab_floor_3', path: `./assets/3d/compressed-textures/${folder}/lab_floor_3.ktx`, type: 'ctexture'},
			{name: 'lab_floor_4', path: `./assets/3d/compressed-textures/${folder}/lab_floor_4.ktx`, type: 'ctexture'},
			{name: 'mine_floor_1', path: `./assets/3d/compressed-textures/${folder}/mine_floor_1.ktx`, type: 'ctexture'},
			{name: 'mine_floor_2', path: `./assets/3d/compressed-textures/${folder}/mine_floor_2.ktx`, type: 'ctexture'},
			{name: 'mine_floor_3', path: `./assets/3d/compressed-textures/${folder}/mine_floor_3.ktx`, type: 'ctexture'},
			{name: 'temple_floor_1', path: `./assets/3d/compressed-textures/${folder}/temple_floor_1.ktx`, type: 'ctexture'},
			{name: 'temple_floor_2', path: `./assets/3d/compressed-textures/${folder}/temple_floor_2.ktx`, type: 'ctexture'},
			{name: 'lab_ceiling', path: `./assets/3d/compressed-textures/${folder}/lab_ceiling.ktx`, type: 'ctexture'},
			{name: 'temple_ceiling', path: `./assets/3d/compressed-textures/${folder}/temple_ceiling.ktx`, type: 'ctexture'},
			{name: 'mine_ceiling', path: `./assets/3d/compressed-textures/${folder}/mine_ceiling.ktx`, type: 'ctexture'},
			{name: 'mine_lava', path: `./assets/3d/compressed-textures/${folder}/mine_lava.ktx`, type: 'ctexture'},
			{name: 'acid', path: `./assets/3d/compressed-textures/${folder}/acid.ktx`, type: 'ctexture'},

			// SPINES
			{name: 'char_ben', type: 'spine'},
			{name: 'char_cannonbolt', type: 'spine'},
			{name: 'char_fourarms', type: 'spine'},
			{name: 'char_heatblast', type: 'spine'},
			{name: 'char_overflow', type: 'spine'},
			{name: 'char_stinkfly', type: 'spine'},

			// WALLS
			{name: 'lab_wall_1', path: './assets/3d/lab_wall_1.jpg', type: 'texture'},
			{name: 'lab_wall_2', path: './assets/3d/lab_wall_2.jpg', type: 'texture'},
			{name: 'lab_wall_3', path: './assets/3d/lab_wall_3.jpg', type: 'texture'},
			{name: 'mine_wall_1', path: './assets/3d/mine_wall_1.jpg', type: 'texture'},
			{name: 'mine_wall_2', path: './assets/3d/mine_wall_2.jpg', type: 'texture'},
			{name: 'temple_wall_1', path: './assets/3d/temple_wall_1.jpg', type: 'texture'},
			{name: 'temple_wall_2', path: './assets/3d/temple_wall_2.jpg', type: 'texture'}
		];

		if (files.length) {
			this._preloader = new PreloaderScene();
			P3.scene.show(this._preloader);

			common.assets.addFiles(threefiles);

			P3.assets.addFiles(files, './assets/');
			P3.assets.signals.progress.add(this.onLoadingProgress, this);
			P3.assets.signals.complete.once(this.onLoadingCompleted, this);

			P3.assets.load();
		} else {
			this.start();
		}
	}

	start() {
		this._app = new Application();
		this._app.init();

		common.t.track('game load duration', {
			gameloadduration: (Date.now() - common.t.loadStart) / 1000
		});

		if (common.config.debug_text) {
			this._statHUD = new StatHUD(1000);
			common.stage.addChild(this._statHUD);
			this._statHUD.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		}
	}

	onLoadingProgress(event) {
		if (this._preloader) {
			this._preloader.loaded = event.progress / 100;
		}
	}

	load3AssetsComplete() {
		this.start();
	}

	onLoadingCompleted() {
		if (this._preloader) {
			this._preloader.loaded = 1;
		}

		common.assets.loadFiles();
	}

	onCanvasResize(correct) {
		if (correct) {
			common.renderer.resize(P3.View.width, P3.View.height);
			common.THREErenderer.setSize(window.innerWidth, window.innerHeight);
			common.THREErenderer.setPixelRatio(window.devicePixelRatio);		// this is improves quality but it causes visual glitches on mobile devices
			common.camera.aspect = window.innerWidth / window.innerHeight;
			common.camera.updateProjectionMatrix();

			if (P3.scene) {
				P3.scene.resize();

				for (let i = 0; i < common.transitions.children.length; ++i) {
					const transition = common.transitions.children[i];

					if (typeof transition.resize === 'function') {
						transition.resize();
					}
				}
			}
		}
	}
}
